import AWS from 'aws-sdk';
import { REACT_APP_ACESS_KEY_ID, REACT_APP_SECRET_ACESS_KEY } from '../Constants/Constants';

// Validate credentials exist
if (!REACT_APP_ACESS_KEY_ID || !REACT_APP_SECRET_ACESS_KEY) {
  console.error('❌ AWS Credentials not found in environment variables!');
  console.error('Please ensure REACT_APP_ACESS_KEY_ID and REACT_APP_SECRET_ACESS_KEY are set in .env');
} else {
  console.log('✅ AWS Credentials loaded successfully');
}

AWS.config.update({
  accessKeyId: REACT_APP_ACESS_KEY_ID,
  secretAccessKey: REACT_APP_SECRET_ACESS_KEY,
  region: 'us-east-1',
  s3ForcePathStyle: false, // Use virtual-hosted-style URLs
});

const s3 = new AWS.S3();
const BUCKET_NAME = 'trainingnexus-dev';
const REGION = 'us-east-1';

// Generate S3 URL in virtual-hosted format: https://bucket.s3.region.amazonaws.com/key
const generateS3Url = (bucketName, key, region) => {
  return `https://${bucketName}.s3.${region}.amazonaws.com/${key}`;
};

// Upload file to S3
export const uploadToS3 = async (file, courseId) => {
  try {
    // Validate inputs
    if (!file) {
      throw new Error('File object is required');
    }
    if (!courseId) {
      throw new Error('Course ID is required');
    }
    if (!REACT_APP_ACESS_KEY_ID || !REACT_APP_SECRET_ACESS_KEY) {
      throw new Error('AWS credentials are missing. Check your .env file for REACT_APP_ACESS_KEY_ID and REACT_APP_SECRET_ACESS_KEY');
    }

    // Use DLP/career-development folder structure
    const fileKey = `DLP/career-development/${file.name}`;

    console.log('📤 Uploading file to S3:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      courseId: courseId,
      bucket: BUCKET_NAME,
      region: REGION,
      s3Key: fileKey
    });
    
    const params = {
      Bucket: BUCKET_NAME,
      Key: fileKey,
      Body: file,
      ACL: 'public-read',
      ContentType: file.type || 'application/octet-stream',
    };

    console.log('📋 S3 Upload Parameters:', {
      Bucket: params.Bucket,
      Key: params.Key,
      ContentType: params.ContentType,
      ACL: params.ACL
    });

    const data = await s3.upload(params).promise();
    
    // Generate the URL in the desired format
    const s3Url = generateS3Url(BUCKET_NAME, fileKey, REGION);
    
    console.log('✅ File uploaded to S3 successfully');
    console.log('📍 S3 URL:', s3Url);
    
    return s3Url;
  } catch (error) {
    console.error('❌ S3 Upload Error:', {
      message: error.message,
      code: error.code,
      statusCode: error.statusCode,
      details: error
    });
    
    // Provide helpful error messages
    if (error.code === 'InvalidAccessKeyId') {
      throw new Error('Invalid AWS Access Key ID. Check your credentials in .env');
    } else if (error.code === 'SignatureDoesNotMatch') {
      throw new Error('Invalid AWS Secret Access Key. Check your credentials in .env');
    } else if (error.code === 'AccessDenied' || error.statusCode === 403) {
      throw new Error(`Access Denied (403). The AWS user doesn't have permission to upload to ${BUCKET_NAME}. Check IAM permissions.`);
    } else if (error.code === 'NoSuchBucket') {
      throw new Error(`Bucket ${BUCKET_NAME} does not exist or you don't have access to it`);
    } else {
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  }
};

// Download/Fetch file from S3
export const fetchFileFromS3 = async (s3Url) => {
  try {
    if (!s3Url) {
      console.warn('S3 URL is empty');
      return null;
    }

    console.log('Fetching file from S3:', s3Url);
    
    // S3 URLs are already public, so just return the URL
    // For display purposes in preview
    return s3Url;
  } catch (error) {
    console.error('Error fetching file from S3:', error);
    throw error;
  }
};

// Get S3 object metadata
export const getS3FileMetadata = async (s3Key) => {
  try {
    const params = {
      Bucket: BUCKET_NAME,
      Key: s3Key,
    };

    const data = await s3.headObject(params).promise();
    return {
      size: data.ContentLength,
      type: data.ContentType,
      lastModified: data.LastModified,
    };
  } catch (error) {
    console.error('Error getting S3 metadata:', error);
    throw error;
  }
};

export default {
  uploadToS3,
  fetchFileFromS3,
  getS3FileMetadata,
};
